import { last } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import DropzoneComponent from 'react-dropzone-component';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import SearchFolderPanel from '~/components/Shared/FolderPanel/FolderPanel';
import PageTitle from '~/components/Shared/PageTitle/PageTitle';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Menu } from '~/eds/src/components/Menu/Menu';
import { Popover } from '~/eds/src/components/Popover/Popover';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { withPermissions } from '~/hocs/withPermissions';
import { withUseEscape } from '~/hocs/withUseEscape';
import { withRouting } from '~/routing/withRouting';
import { filterValidFiles } from '~/utils/helper.utils';

import {
  MODAL_ACCESS_SETTINGS,
  MODAL_DELETE,
  MODAL_FOLDER_TREE,
  MODAL_NAME,
  MODAL_UPLOAD,
} from '../../../types/modal.types';
import { featureFlagIncluded } from '../../../utils/user';
import EcBreadcrumbs from '../../Shared/EcBreadcrumbs/EcBreadcrumbs';
import EcFolderPanel from '../../Shared/EcFolderPanel/EcFolderPanel';
import withHover from '../../Shared/HOCs/withHover/withHover';
import FolderLookUpIcon from '../../Shared/Icons/FolderLookUpIcon';
import FolderUpIcon from '../../Shared/Icons/FolderUpIcon';
import styles from './DocumentsPageHeader.styles';

const FolderUpIconWithHover = withHover(FolderUpIcon);
const FolderLookUpIconWithHover = withHover(FolderLookUpIcon);
const FolderPanel = withUseEscape(EcFolderPanel);

const folderAccessPermission = {
  resourceId: 'folder_access',
  resourceType: 'edit',
};

const rootFolderAccessPermission = {
  resourceId: 'root_folder_access_settings',
  resourceType: 'view',
};
const permissionsToCheck = [folderAccessPermission, rootFolderAccessPermission];

class DocumentsPageHeader extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      folderPanelShown: false,
    };

    this.folderPanel = React.createRef();

    this.navigate = this.navigate.bind(this);
    this.handleOnBackClick = this.handleOnBackClick.bind(this);
    this.showFolderPanel = this.showFolderPanel.bind(this);
    this.hideFolderPanel = this.hideFolderPanel.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleMouseDown);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleMouseDown);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.folderData !== this.props.folderData) {
      this.setState({ folderPanelShown: false });
    }
  }

  navigate(path) {
    this.props.navigate(path);
  }

  handleMouseDown(e) {
    if (!this.folderPanel || !this.folderPanel.current.contains(e.target)) {
      this.hideFolderPanel();
    }
  }

  handleOnBackClick(parentId) {
    this.props.navigate(`/documents/${parentId}`);
  }

  showFolderPanel() {
    this.setState({ folderPanelShown: true });
  }

  hideFolderPanel() {
    this.setState({ folderPanelShown: false });
  }

  handleNavigateToFolder = (folder) => {
    if (folder) {
      this.props.navigate(`/documents/${folder.id}`);
    }
  };

  renderFolderPanel() {
    const { folderData, folderPathId } = this.props;

    return (
      <FolderPanel
        folderPathId={folderPathId}
        isRootFolder={!folderData.path || !folderData.path.length}
        hidePanel={() => this.setState({ folderPanelShown: false })}
        onEsc={this.hideFolderPanel}
      />
    );
  }

  renderSearchFolderPanel = () => {
    const { folderData } = this.props;
    return (
      <Box h={400}>
        <SearchFolderPanel
          onSelect={this.handleNavigateToFolder}
          isRootFolder={!folderData.path || !folderData.path.length}
          folderPathId={folderData?.id}
          w={600}
        />
      </Box>
    );
  };

  handleContextMenuOptionSelect = (menuOption) => {
    const { handleShowModal, folderData } = this.props;
    switch (menuOption.value) {
      case 'rename':
        handleShowModal(MODAL_NAME, {
          type: 'folder',
          id: folderData.id,
          name: folderData.name,
          selfUpdate: true,
          parentId: last(folderData.path ?? [])?.id,
        });
        break;
      case 'move':
        handleShowModal(MODAL_FOLDER_TREE, {
          type: 'folder',
          id: folderData.id,
          name: folderData.name,
          action: 'Move',
          selfUpdate: true,
          parentId: last(folderData.path ?? [])?.id,
          contains_sync_pair: folderData.contains_sync_pair,
        });
        break;
      case 'accessSettings':
        handleShowModal(MODAL_ACCESS_SETTINGS, {
          type: 'folder',
          id: folderData.id,
          name: folderData.name,
          parentId: last(folderData.path ?? [])?.id,
          folder_access_list: folderData.folder_access_list,
          content_visibility_level: folderData.content_visibility_level,
          selfUpdate: true,
        });
        break;
      case 'rootFolderAccessSettings':
        handleShowModal(MODAL_ACCESS_SETTINGS, {
          type: 'folder',
          id: folderData.id,
          name: 'root',
          parentId: null,
          folder_access_list: folderData.folder_access_list,
          content_visibility_level: folderData.content_visibility_level,
          selfUpdate: true,
          isRoot: true,
        });
        break;
      case 'delete':
        handleShowModal(MODAL_DELETE, {
          type: 'folder',
          id: folderData.id,
          name: folderData.name,
          parentId: last(folderData.path ?? [])?.id,
          selfUpdate: true,
        });
        break;
      default:
        break;
    }
  };

  renderHeaderActions() {
    const {
      handleShowModal,
      handleAddUploadFiles,
      user,
      canModifyFolder,
      folderData,
      intl,
    } = this.props;

    const componentConfig = { postUrl: 'no-url' };
    const djsConfig = {
      autoQueue: false,
      autoProcessQueue: false,
      clickable: '#upload_documents',
      dictDefaultMessage: '',
      previewTemplate: '<div class="ignore"></div>',
    };
    const eventHandlers = {
      init: (dz) => (this.dropzone = dz),
      addedfiles: async (files) => {
        const validFiles = await filterValidFiles(files);
        setTimeout(() => {
          handleAddUploadFiles(validFiles);
          handleShowModal(MODAL_UPLOAD);
        }, 0);
      },
    };

    const hasFolderAccessPermission = this.props.checkPermission(
      folderAccessPermission.resourceId,
      folderAccessPermission.resourceType,
    );

    const hasRootFolderAccessSettingsPermission = this.props.checkPermission(
      rootFolderAccessPermission.resourceId,
      rootFolderAccessPermission.resourceType,
    );

    const menuOptions = [
      {
        label: intl.formatMessage({
          id: 'DocumentsPageHeader.renameOption',
          defaultMessage: 'Rename',
          description: 'Menu option to rename folder',
        }),
        value: 'rename',
      },
      {
        label: intl.formatMessage({
          id: 'DocumentsPageHeader.moveOption',
          defaultMessage: 'Move',
          description: 'Menu option to move folder',
        }),
        value: 'move',
      },
      {
        label: intl.formatMessage({
          id: 'DocumentsPageHeader.accessSettingsOption',
          defaultMessage: 'Access Settings',
          description: 'Menu option for folder access settings',
        }),
        value: 'accessSettings',
        hidden: !hasFolderAccessPermission,
      },
      {
        label: intl.formatMessage({
          id: 'DocumentsPageHeader.deleteOption',
          defaultMessage: 'Delete',
          description: 'Menu option to delete folder',
        }),
        value: 'delete',
      },
    ];

    return (
      <Layout preset="buttons">
        {this.renderFolderSearchAction(
          <Button
            text={intl.formatMessage({
              id: 'DocumentsPageHeader.searchFoldersButton',
              defaultMessage: 'Search Folders',
              description: 'Button text for searching folders',
            })}
          />,
        )}
        {canModifyFolder ? (
          <Button
            id="documents_header_newFolderButton"
            onClick={() => handleShowModal(MODAL_ACCESS_SETTINGS)}
            text={intl.formatMessage({
              id: 'DocumentsPageHeader.newFolderButton',
              defaultMessage: 'New Folder',
              description: 'Button text for creating new folder',
            })}
          />
        ) : null}
        {canModifyFolder && featureFlagIncluded(user, 'UPLOAD') ? (
          <DropzoneComponent
            className="document-page-header-dropzone"
            config={componentConfig}
            djsConfig={djsConfig}
            eventHandlers={eventHandlers}
          >
            <div id="upload_documents">
              <Button
                id={OnboardingIdType.DocumentUpload}
                text={intl.formatMessage({
                  id: 'DocumentsPageHeader.uploadButton',
                  defaultMessage: 'Upload',
                  description: 'Button text for uploading documents',
                })}
                variant="primary"
              />
            </div>
          </DropzoneComponent>
        ) : null}
        {folderData.path?.length > 0 ? (
          <Menu
            trigger={
              <Icon
                icon="more"
                tooltip={intl.formatMessage({
                  id: 'DocumentsPageHeader.moreTooltip1',
                  defaultMessage: 'More',
                  description: 'Tooltip for more options menu at folder level',
                })}
              />
            }
            options={menuOptions.filter((option) => !option.hidden)}
            onSelectOption={this.handleContextMenuOptionSelect}
          />
        ) : hasRootFolderAccessSettingsPermission &&
          folderData.path?.length === 0 ? (
          <ActionsMenu
            icon="more"
            tooltip={intl.formatMessage({
              id: 'DocumentsPageHeader.moreTooltip2',
              defaultMessage: 'More',
              description: 'Tooltip for more options menu at root level',
            })}
            actions={[
              {
                options: [
                  {
                    label: intl.formatMessage({
                      id: 'DocumentsPageHeader.rootFolderAccessSettings',
                      defaultMessage: 'Root Folder Access Settings',
                      description:
                        'Menu option for root folder access settings',
                    }),
                    value: 'rootFolderAccessSettings',
                    onClick: this.handleContextMenuOptionSelect,
                  },
                ],
              },
            ]}
          />
        ) : null}
      </Layout>
    );
  }

  renderFolderSearchAction(overrideTrigger) {
    const { classes, intl } = this.props;
    const trigger = overrideTrigger ?? (
      <button
        aria-haspopup="true"
        title={intl.formatMessage({
          id: 'DocumentsPageHeader.goToFolderTitle',
          defaultMessage: 'Go to a folder',
          description: 'Title for go to folder button',
        })}
        className={classes.folderIcon}
      >
        {<FolderLookUpIconWithHover size="32" />}
      </button>
    );

    return (
      <Popover preset="dropdown" trigger={trigger}>
        {this.renderSearchFolderPanel()}
      </Popover>
    );
  }

  renderFullTitle() {
    const { classes, folderData } = this.props;
    const { folderPanelShown } = this.state;
    return (
      <div className={classes.documentFullTitle}>
        {folderData.name && folderData.path?.length ? (
          <EcBreadcrumbs path={folderData.path} />
        ) : null}
        <Layout align="center" spacing={2}>
          {folderData.name ? (
            <PageTitle title={folderData.name} />
          ) : (
            <div className={classes.emptyfolderName} />
          )}
        </Layout>
        <div ref={this.folderPanel}>
          {folderPanelShown ? this.renderFolderPanel() : null}
        </div>
      </div>
    );
  }

  renderBackIcon() {
    const { classes, folderData, intl } = this.props;

    if (folderData.path) {
      if (folderData.path.length > 0) {
        const parentId = folderData.path[folderData.path.length - 1].id;
        return (
          <button
            className={classes.documentBackAction}
            onClick={() => {
              this.handleOnBackClick(parentId);
            }}
            title={intl.formatMessage(
              {
                id: 'DocumentsPageHeader.goToParentFolderTitle',
                defaultMessage: 'go to the parent folder of {folderName}',
                description:
                  'Title for go to parent folder button. Example: folderName = "Legal Documents"',
              },
              {
                folderName: folderData.name,
              },
            )}
          >
            <FolderUpIconWithHover />
          </button>
        );
      } else {
        return null;
      }
    }
  }

  render() {
    const { classes, loading, user } = this.props;

    return (
      <div className={classes.documentHeaderContainer}>
        <div className={classes.documentHeader}>
          {this.renderBackIcon()}
          {this.renderFullTitle()}
          {!loading && Object.keys(user).length
            ? this.renderHeaderActions()
            : null}
        </div>
      </div>
    );
  }
}

DocumentsPageHeader.propTypes = {
  folderData: PropTypes.object.isRequired,
  handleShowModal: PropTypes.func,
  user: PropTypes.object,
};

export default injectSheet(styles)(
  withRouting(
    withPermissions(
      withCurrentUser(injectIntl(DocumentsPageHeader)),
      permissionsToCheck,
    ),
  ),
);
